import React, { useEffect, useRef, useState, useCallback } from "react";
import { LiveAvatarSession, SessionState, VoiceChatState } from "@heygen/liveavatar-web-sdk";
import { createLiveAvatarSessionToken, stopLiveAvatarSession } from "../../../utils/api";
import { LIVEAVATAR_CONFIG } from "../../../constants";
import { useLocalization } from "../../../context/LocalizationContext";
import { logSecretly } from "../../../utils/logSecretly";
import "./LiveAvatar.css";

const LiveAvatarComponent = ({ sessionToken, onClose }) => {
  const { t } = useLocalization();
  const [message, setMessage] = useState("");
  const [sessionState, setSessionState] = useState(SessionState.INACTIVE);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [mode, setMode] = useState("chat"); // "chat" or "speak"
  const [voiceChatState, setVoiceChatState] = useState(VoiceChatState.INACTIVE);
  const [isLocalizationReady, setIsLocalizationReady] = useState(false);
  const [micPermissionError, setMicPermissionError] = useState(false);
  const videoRef = useRef(null);
  const sessionRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // Keep onCloseRef updated with latest onClose without triggering effect re-runs
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Wait for localization to be ready before showing text (prevents flash)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLocalizationReady(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Check microphone permission status
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        // Check if Permissions API is available
        if (navigator.permissions && navigator.permissions.query) {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
          
          // Set initial state
          setMicPermissionError(permissionStatus.state === 'denied');
          
          // Listen for permission changes
          permissionStatus.onchange = () => {
            setMicPermissionError(permissionStatus.state === 'denied');
          };
        }
      } catch (err) {
        // Permissions API might not be supported or microphone query might fail
        // In this case, we'll detect permission errors when trying to start voice chat
        console.log('Could not check microphone permission:', err);
      }
    };

    checkMicPermission();
  }, []);

  useEffect(() => {
    if (!sessionToken) {
      return;
    }
    
    // Create session instance
    const session = new LiveAvatarSession(sessionToken);
    sessionRef.current = session;
    
    let isAttached = false;

    // Set up event listeners
    session.on('session.state_changed', (state) => {
      setSessionState(state);
      
      if (state === SessionState.DISCONNECTED) {
        onCloseRef.current(); // Use ref to get latest callback without triggering re-runs
      }
    });

    // Listen for stream ready event
    session.on('session.stream_ready', () => {
      if (!isAttached && videoRef.current) {
        try {
          session.attach(videoRef.current);
          isAttached = true;
          setIsVideoReady(true);
        } catch (err) {
          console.error('❌ [LiveAvatar] Failed to attach video:', err);
        }
      }
    });

    // Track any errors
    session.on('error', (error) => {
      console.error('❌ [LiveAvatar] Session error:', error);
    });

    // Monitor voice chat state
    let voiceChatInterval = null;
    if (session.voiceChat) {
      const voiceChat = session.voiceChat;
      
      voiceChatInterval = setInterval(() => {
        if (voiceChat.state !== undefined) {
          setVoiceChatState(voiceChat.state);
        }
      }, 500);
    }

    // Start the session
    session.start()
      .catch((err) => {
        console.error('❌ [LiveAvatar] Failed to start session:', err);
        setSessionState(SessionState.DISCONNECTED);
      });

    // Cleanup on unmount
    return () => {
      if (voiceChatInterval) {
        clearInterval(voiceChatInterval);
      }
      if (session) {
        session.stop().catch(console.error);
      }
    };
  }, [sessionToken]); // Removed onClose from dependencies - using ref instead

  const handleSendMessage = useCallback(() => {
    const connected = sessionState === SessionState.CONNECTED;
    
    if (message.trim() && sessionRef.current) {
      const messageToSend = message.trim();
      
      try {
        sessionRef.current.message(messageToSend);
        setMessage("");
      } catch (err) {
        console.error('❌ [LiveAvatar] Failed to send message:', {
          error: err.message,
          stack: err.stack
        });
      }
    }
  }, [message, sessionState, mode]);

  const handleStartVoiceChat = useCallback(async () => {
    if (sessionRef.current && sessionRef.current.voiceChat) {
      try {
        setMicPermissionError(false); // Reset error
        await sessionRef.current.voiceChat.start();
      } catch (err) {
        // Check if it's a permission error
        if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
          setMicPermissionError(true);
        }
        console.error('❌ [LiveAvatar] Failed to start voice chat:', {
          error: err.message,
          name: err.name,
          stack: err.stack
        });
      }
    }
  }, [voiceChatState, sessionState]);

  const handleStopVoiceChat = useCallback(() => {
    if (sessionRef.current && sessionRef.current.voiceChat) {
      try {
        sessionRef.current.voiceChat.stop();
      } catch (err) {
        console.error('❌ [LiveAvatar] Failed to stop voice chat:', err);
      }
    }
  }, []);


  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const isConnected = sessionState === SessionState.CONNECTED;
  const isVoiceChatActive = voiceChatState === VoiceChatState.ACTIVE;

  return (
    <div className="liveavatar-container">
      <div className="liveavatar-video-wrapper">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="liveavatar-video"
          style={{ 
            opacity: isVideoReady ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        />
        
        {/* Show loading spinner until video is ready */}
        {!isVideoReady && (
          <div className="liveavatar-loading">
            <div className="loading-spinner"></div>
            {isLocalizationReady && <p>{t('liveAvatar.connectingToAvatar')}</p>}
          </div>
        )}
        
        {/* Status indicators - only show when disconnected */}
        {isVideoReady && sessionState === SessionState.DISCONNECTED && (
          <div className="liveavatar-status">
            <span className="status-disconnected">{t('liveAvatar.disconnected')}</span>
          </div>
        )}
      </div>

      {/* Interaction interface - only show when video is ready */}
      {isVideoReady && (
        <div className="liveavatar-chat">
          {/* Mode Toggle */}
          <div className="mode-toggle">
            <button
              className={`mode-btn ${mode === 'chat' ? 'active' : ''}`}
              onClick={() => {
                setMode('chat');
                setMicPermissionError(false); // Clear error when switching modes
                if (isVoiceChatActive) {
                  handleStopVoiceChat();
                }
              }}
            >
              {t('liveAvatar.chat')}
            </button>
            <button
              className={`mode-btn ${mode === 'speak' ? 'active' : ''} ${isVoiceChatActive ? 'listening' : ''}`}
              onClick={() => {
                if (mode === 'speak' && isVoiceChatActive) {
                  // If already in speak mode and active, stop it
                  handleStopVoiceChat();
                } else {
                  // Switch to speak mode and start voice chat
                  setMode('speak');
                  if (!isVoiceChatActive) {
                    handleStartVoiceChat();
                  }
                }
              }}
              disabled={!isConnected || micPermissionError}
            >
              {isVoiceChatActive ? t('liveAvatar.listening') : t('liveAvatar.speak')}
            </button>
          </div>

          {/* Microphone Permission Error - show when speak button is disabled due to permission */}
          {micPermissionError && (
            <div className="mic-permission-error">
              <p>{t('liveAvatar.micPermissionMessage')}</p>
            </div>
          )}

          {/* Chat Mode */}
          {mode === 'chat' && (
            <div className="chat-input-container">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('liveAvatar.typeMessage')}
                className="chat-input"
                disabled={!isConnected}
              />
              <button
                onClick={handleSendMessage}
                className="chat-send-btn"
                disabled={!message.trim() || !isConnected}
              >
                {t('liveAvatar.send')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const LiveAvatar = ({ onClose }) => {
  const { t } = useLocalization();
  const [avatarLanguage, setAvatarLanguage] = useState(null); // null = not selected yet
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const initializeSession = async (selectedLanguage) => {
    try {
      setIsLoading(true);
      setError(null);
      
      logSecretly('🔍 [LiveAvatar] Config check:', {
        API_KEY_present: !!LIVEAVATAR_CONFIG.API_KEY,
        API_KEY_length: LIVEAVATAR_CONFIG.API_KEY?.length,
        AVATAR_ID: LIVEAVATAR_CONFIG.AVATAR_ID,
        VOICE_ID: LIVEAVATAR_CONFIG.VOICE_ID,
        CONTEXT_ID: LIVEAVATAR_CONFIG.CONTEXT_ID,
        API_URL: LIVEAVATAR_CONFIG.API_URL
      });
      
      const tokenData = await createLiveAvatarSessionToken(selectedLanguage, 'FULL');
      
      setSessionToken(tokenData.session_token);
      setSessionId(tokenData.session_id);
      setAvatarLanguage(selectedLanguage);
    } catch (err) {
      console.error('❌ [LiveAvatar] Failed to initialize session:', err);
    
      setError(t('liveAvatar.failedToConnect'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageSelect = (lang) => {
    initializeSession(lang);
  };

  const handleClose = async () => {
    if (sessionToken && sessionId) {
      try {
        await stopLiveAvatarSession(sessionToken, sessionId);
      } catch (err) {
        console.error('Error stopping session:', err);
      }
    }
    onClose();
  };

  // Show language selector if language hasn't been selected yet
  if (!avatarLanguage && !isLoading && !error) {
    return (
      <div className="liveavatar-container">
        <div className="liveavatar-video-wrapper language-select-wrapper">
          <div className="language-select-content">
            <h2>{t('liveAvatar.selectAvatarLanguage')}</h2>
            <p className="language-selector-subtitle">{t('liveAvatar.avatarWillSpeak')}</p>
            <div className="language-options">
              <button 
                className="language-option-btn"
                onClick={() => handleLanguageSelect('en')}
              >
                <span className="language-name">{t('language.english')}</span>
              </button>
              <button 
                className="language-option-btn"
                onClick={() => handleLanguageSelect('ar')}
              >
                <span className="language-name">{t('language.arabic')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="liveavatar-loading">
        <div className="loading-spinner"></div>
        <p>{t('liveAvatar.connectingToAvatar')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="liveavatar-error">
        <p>{error}</p>
        <button onClick={() => setAvatarLanguage(null)} className="retry-btn">
          {t('liveAvatar.retry')}
        </button>
        <button onClick={onClose} className="close-btn">
          {t('liveAvatar.close')}
        </button>
      </div>
    );
  }

  if (!sessionToken) {
    return null;
  }

  return (
    <LiveAvatarComponent 
      sessionToken={sessionToken} 
      onClose={handleClose}
    />
  );
};

export default LiveAvatar;
